/**
 * Minimal markdown renderer for admin-authored content.
 *
 * Supported:
 *   paragraphs (blank line → new paragraph)
 *   bullet lists (each line starts with `- ` or `* `)
 *   **bold**
 *   *italic*   (also _italic_)
 *   single-line break inside a paragraph → <br>
 *
 * Output is React elements, so text is auto-escaped — no dangerouslySetInnerHTML,
 * no XSS risk even if an admin pastes raw HTML.
 */

function renderInline(text, keyPrefix) {
  // Split by tokens: **bold**, *italic*, _italic_ — preserve the delimiters so
  // we can decide what to wrap.
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*|_[^_]+_)/g);
  return parts.map((p, i) => {
    const key = `${keyPrefix}-${i}`;
    if (!p) return null;
    if (p.startsWith("**") && p.endsWith("**") && p.length > 4) {
      return <strong key={key}>{p.slice(2, -2)}</strong>;
    }
    if (p.startsWith("*") && p.endsWith("*") && p.length > 2) {
      return <em key={key}>{p.slice(1, -1)}</em>;
    }
    if (p.startsWith("_") && p.endsWith("_") && p.length > 2) {
      return <em key={key}>{p.slice(1, -1)}</em>;
    }
    return <span key={key}>{p}</span>;
  });
}

function renderParagraph(text, key) {
  // Within a paragraph, turn single newlines into <br/>.
  const lines = text.split("\n");
  const out = [];
  lines.forEach((ln, i) => {
    out.push(...renderInline(ln, `${key}-l${i}`));
    if (i < lines.length - 1) out.push(<br key={`${key}-br${i}`} />);
  });
  return <p key={key}>{out}</p>;
}

export default function Markdown({ text, className }) {
  if (!text) return null;

  // Normalise newlines and split into blocks on blank lines.
  const blocks = String(text).replace(/\r\n/g, "\n").split(/\n{2,}/);

  const rendered = blocks.map((block, bi) => {
    const lines = block.split("\n");
    const isBulletBlock =
      lines.length > 0 &&
      lines.every(ln => /^\s*[-*]\s+/.test(ln) || ln.trim() === "");
    const bulletLines = lines.filter(ln => /^\s*[-*]\s+/.test(ln));

    if (isBulletBlock && bulletLines.length > 0) {
      return (
        <ul key={`ul-${bi}`}>
          {bulletLines.map((ln, li) => {
            const content = ln.replace(/^\s*[-*]\s+/, "");
            return <li key={`li-${bi}-${li}`}>{renderInline(content, `li-${bi}-${li}`)}</li>;
          })}
        </ul>
      );
    }

    return renderParagraph(block, `p-${bi}`);
  });

  return <div className={className}>{rendered}</div>;
}
